import type { RegionTypes } from '../../Enums/RegionTypes';
import type { ClipSoftness } from '../../types';
import type { Box } from '../Shared/Box';
import type { Position } from '../Shared/Position';
import type { Region } from '../Shared/Region';

export type AnimationTarget = {
    opacity: number;
    scale: number;
    rotation: number;
    blur: number;
    motionBlur: boolean;
    boundingBox: Box;
    contentBox: Box;
    getAbsContentPosition(): Position;
    getAbsBoxPosition(): Position;
    getRegion(type: RegionTypes): Region;
};

export type AnimatableTarget = AnimationTarget & {
    clipPath?: string | null;
    clipSoftness?: ClipSoftness;
    translatedX?: number;
    translatedY?: number;
};

export type AnimationChanges = {
    opacity?: number;
    scale?: number;
    rotation?: number;
    blur?: number;
    clipPath?: string;
    clipSoftness?: ClipSoftness;
    boxPosition?: { x: number; y: number };
    translatedX?: number;
    translatedY?: number;
    motionBlur?: boolean;
};

export const applyAnimationChanges = (target: AnimatableTarget, changes: AnimationChanges) => {
    if (changes.opacity !== undefined) {
        target.opacity = changes.opacity;
    }

    if (changes.scale !== undefined) {
        target.scale = changes.scale;
    }

    if (changes.rotation !== undefined) {
        target.rotation = changes.rotation;
    }

    if (changes.blur !== undefined) {
        target.blur = changes.blur;
    }

    if (changes.clipPath !== undefined) {
        target.clipPath = changes.clipPath;
    }

    if ('clipSoftness' in changes) {
        target.clipSoftness = changes.clipSoftness;
    }

    if (changes.boxPosition) {
        target.boundingBox.position.setX(changes.boxPosition.x);
        target.boundingBox.position.setY(changes.boxPosition.y);
    }

    if (changes.translatedX !== undefined) {
        target.translatedX = changes.translatedX;
    }

    if (changes.translatedY !== undefined) {
        target.translatedY = changes.translatedY;
    }

    if (changes.motionBlur !== undefined) {
        target.motionBlur = changes.motionBlur;
    }
};

export const mergeAnimationChanges = (...changes: (AnimationChanges | null)[]): AnimationChanges =>
    Object.assign({}, ...changes.filter(Boolean));
